// AXIS ZERO - Custom Hook for Game Loop
// Handles canvas setup, game initialization, audio, and cleanup
// FIXED: Throttled state updates, proper cleanup, StrictMode safe

import { useRef, useEffect, useCallback, useState } from 'react';
import { GameLoop, GAME_STATES, GAME_MODES, audioManager } from '../game';

export const useGameLoop = () => {
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const containerRef = useRef(null);
  
  // Guards for StrictMode double-mount
  const initializedRef = useRef(false);
  const cleanedUpRef = useRef(false);
  
  // Throttled score ref (updated every frame, state updated less often)
  const scoreRef = useRef(0);
  const streakRef = useRef(0);
  const isHighStreakRef = useRef(false);
  const lastScoreUpdateRef = useRef(0);
  const scoreUpdateIntervalRef = useRef(null);
  
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [gameMode, setGameMode] = useState(GAME_MODES.NORMAL);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [isHighStreak, setIsHighStreak] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const [gameOverData, setGameOverData] = useState(null);
  const [isMuted, setIsMuted] = useState(audioManager.isMuted);
  const [audioInitialized, setAudioInitialized] = useState(false);

  // Initialize game - StrictMode safe
  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (initializedRef.current) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    initializedRef.current = true;
    cleanedUpRef.current = false;

    const game = new GameLoop(canvas);
    gameRef.current = game;

    // Set up throttled score callback - updates ref immediately, 
    // but React state is updated via interval
    game.onScoreUpdate = (newScore) => {
      scoreRef.current = newScore;
      // Also update streak info
      if (game.streakSystem) {
        const info = game.streakSystem.getDisplayInfo();
        streakRef.current = info.streak;
        isHighStreakRef.current = info.isHighMode;
      }
    };
    
    game.onNearMiss = (result) => {
      // Update streak immediately on near miss
      if (result) {
        streakRef.current = result.streak;
        isHighStreakRef.current = game.streakSystem?.isInHighMode() || false;
      }
    };

    game.onGameOver = (data) => {
      // Clear score update interval on game over
      if (scoreUpdateIntervalRef.current) {
        clearInterval(scoreUpdateIntervalRef.current);
        scoreUpdateIntervalRef.current = null;
      }
      
      // Final score update
      setScore(data.score);
      setGameState(GAME_STATES.GAME_OVER);
      setGameOverData(data);
      setIsNewHighScore(data.isNewHighScore);
      setHighScore(data.highScore);
    };

    // Initial high score
    setHighScore(game.getHighScore());

    // Cleanup function
    return () => {
      cleanedUpRef.current = true;
      
      // Clear score update interval
      if (scoreUpdateIntervalRef.current) {
        clearInterval(scoreUpdateIntervalRef.current);
        scoreUpdateIntervalRef.current = null;
      }
      
      // Stop game loop
      if (gameRef.current) {
        gameRef.current.stop();
      }
      
      // Allow re-initialization on next mount
      initializedRef.current = false;
    };
  }, []);

  // Handle resize - with proper cleanup
  useEffect(() => {
    const handleResize = () => {
      const container = containerRef.current;
      const canvas = canvasRef.current;
      const game = gameRef.current;
      
      if (!container || !canvas || !game) return;

        const { width, height } = container.getBoundingClientRect();
        // Canvas sizing is handled in GameCanvas.jsx (single source of truth)
        game.handleResize(width, height);
    };

    const handleOrientationChange = () => {
      setTimeout(handleResize, 100);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Initialize audio on first user interaction
  const initAudio = useCallback(async () => {
    if (!audioInitialized) {
      await audioManager.init();
      await audioManager.resumeContext();
      setAudioInitialized(true);
      setIsMuted(audioManager.isMuted);
    }
  }, [audioInitialized]);

  // Start throttled score updates
  const startScoreUpdates = useCallback(() => {
    // Clear any existing interval
    if (scoreUpdateIntervalRef.current) {
      clearInterval(scoreUpdateIntervalRef.current);
    }
    
    // Update React state every 100ms (10fps for UI)
    scoreUpdateIntervalRef.current = setInterval(() => {
      if (!cleanedUpRef.current) {
        setScore(scoreRef.current);
        setStreak(streakRef.current);
        setIsHighStreak(isHighStreakRef.current);
      }
    }, 100);
  }, []);

  // Stop score updates
  const stopScoreUpdates = useCallback(() => {
    if (scoreUpdateIntervalRef.current) {
      clearInterval(scoreUpdateIntervalRef.current);
      scoreUpdateIntervalRef.current = null;
    }
  }, []);

  // Start game
  const startGame = useCallback(async (mode = GAME_MODES.NORMAL) => {
    const game = gameRef.current;
    if (!game) return;

    // Initialize audio on first start
    await initAudio();
    
    audioManager.playStartConfirm();
    
    // Reset score ref
    scoreRef.current = 0;
    streakRef.current = 0;
    isHighStreakRef.current = false;
    setScore(0);
    setStreak(0);
    setIsHighStreak(false);
    
    // Start throttled score updates
    startScoreUpdates();
    
    game.start(mode);
    setGameState(GAME_STATES.PLAYING);
    setGameMode(mode);
    setIsNewHighScore(false);
    setGameOverData(null);
  }, [initAudio, startScoreUpdates]);

  // Handle jump/tap
  const handleTap = useCallback(async () => {
    const game = gameRef.current;
    if (!game) return;

    // Initialize audio on first tap
    await initAudio();

    const currentState = game.getState();
    
    if (currentState === GAME_STATES.PLAYING) {
      game.jump();
    } else if (currentState === GAME_STATES.GAME_OVER) {
      audioManager.playRestart();
      startGame(game.getMode());
    } else if (currentState === GAME_STATES.READY) {
      startGame();
    }
  }, [startGame, initAudio]);

  // Restart game
  const restartGame = useCallback(async (mode) => {
    audioManager.playRestart();
    await startGame(mode || gameMode);
  }, [startGame, gameMode]);

  // Toggle sound
  const toggleSound = useCallback(async () => {
    await initAudio();
    audioManager.playButtonClick();
    const soundOn = audioManager.toggleMute();
    setIsMuted(!soundOn);
    return soundOn;
  }, [initAudio]);

  // Start practice mode
  const startPracticeMode = useCallback(async () => {
    await startGame(GAME_MODES.PRACTICE);
  }, [startGame]);

  // Exit practice mode - return to main menu
  const exitPractice = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;

    // Stop score updates
    stopScoreUpdates();

    // Stop game loop
    game.stop();
    
    // Reset game state
    game.reset();
    
    // Stop music gracefully
    audioManager.stopMusic();
    
    // Play button click sound
    audioManager.playButtonClick();
    
    // Return to ready state
    setGameState(GAME_STATES.READY);
    setGameMode(GAME_MODES.NORMAL);
    setScore(0);
    setStreak(0);
    setIsHighStreak(false);
    scoreRef.current = 0;
    streakRef.current = 0;
    isHighStreakRef.current = false;
    setGameOverData(null);
  }, [stopScoreUpdates]);

  // Restart practice mode
  const restartPractice = useCallback(async () => {
    audioManager.playRestart();
    await startGame(GAME_MODES.PRACTICE);
  }, [startGame]);

  return {
    canvasRef,
    containerRef,
    gameState,
    gameMode,
    score,
    streak,
    isHighStreak,
    highScore,
    isNewHighScore,
    gameOverData,
    isMuted,
    startGame,
    handleTap,
    restartGame,
    toggleSound,
    startPracticeMode,
    initAudio,
    exitPractice,
    restartPractice,
  };
};

export default useGameLoop;
